import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FlexflowCardStatus } from 'src/modules/Flexflow/types';
import { addToast } from 'src/old/actions';
import { useTerminateCardMutation, useUpdateCardStatusMutation } from 'src/shared/api/flexflow';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import { type CardStatusToDisplay } from '../../types';
import { selectIsLoading } from 'src/old/actions/loading/loadingSelectors';

type UseActionsSectionProps = {
  cardStatus?: CardStatusToDisplay;
  payflowUserId: number;
};

export const useActionsSection = ({ cardStatus, payflowUserId }: UseActionsSectionProps) => {
  const { t } = useTranslation(['flexflow']);
  const dispatch = useDispatch();
  const cardFrozen = cardStatus === FlexflowCardStatus.SUSPENDED;
  const [updateCardStatus] = useUpdateCardStatusMutation();
  const [terminateCard] = useTerminateCardMutation();
  const { values } = useFormState();
  const isLoading = useSelector(selectIsLoading);
  const hasPhysicalCard =
    !!values?.card?.physicalCard?.status && values?.card?.physicalCard?.status !== FlexflowCardStatus.TERMINATED;

  const frWalletTotals = values?.frWalletTotals;
  const totalWalletAmount =
    Number(frWalletTotals?.currentlyAvailable?.food ?? 0) + Number(frWalletTotals?.currentlyAvailable?.transport ?? 0);
  const hasWalletFunds = totalWalletAmount > 0;

  const toggleFreezeCard = async () => {
    let cardStatusToUpdate;

    if (cardFrozen) {
      cardStatusToUpdate = FlexflowCardStatus.ACTIVE;
    } else {
      cardStatusToUpdate = FlexflowCardStatus.SUSPENDED;
    }

    await updateCardStatus({ employeeId: payflowUserId, status: cardStatusToUpdate, cardMedium: 'virtual' });
  };

  const handleTerminateCardConfirmClick = async (cardMedium: 'physical' | 'virtual') => {
    if (!values) {
      return;
    }

    try {
      const result = await terminateCard({
        employeeId: values.payflowUserId,
        cardMedium
      });

      if ('error' in result) {
        throw new Error('Error terminating card');
      }

      dispatch(
        addToast({
          ...toastTypes.success,
          defaultMessage: t('flexflow.updated successfully The changes will be reflected in a few minutes', {
            ns: 'actions'
          })
        })
      );
    } catch (error) {
      console.error(error);

      dispatch(
        addToast({
          ...toastTypes.error,
          defaultMessage: error instanceof Error ? error.message : 'Error terminating card'
        })
      );
    }
  };

  return {
    isLoading,
    values,
    cardFrozen,
    hasPhysicalCard,
    hasWalletFunds,
    toggleFreezeCard,
    handleTerminateCardConfirmClick
  };
};
